import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { enqueueSnackbar } from "notistack";
import { Button, Box } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { AppState } from "../../../../../redux";
import { fetchMembershipsByOrganizationId } from "../../../../../redux/organizationMembership/action";
import { fetchOrganizationEventsForBooking } from "../../../../../redux/organizationBookings/action";
import { organizationBulkBookingService } from "../../../../../api";
import CustomMultiSelectAutocomplete from "../../../../forms/selects/CustomMultiSelectAutocomplete";
import { OrganizationBookingFailData } from "../../../../../models/organizationBulkBookingData";
import OrganizationBookingFailureNotedDialog from "../../../../layout/dialog/OrganizationBookingFailureNotedDialog";
import Loader from "../../../../theming/loader/Loader";

/**
 * @interface AddMembershipProps
 */
interface AddMembershipProps {
  organizationId: string;
  onClose: () => void;
}

/**
 * @interface OrganizationBookingComponent
 */
interface OrganizationBookingComponent {
  id: string;
  name: string;
}

const useStyles = makeStyles(() =>
  createStyles({
    contentWrapper: {
      position: "relative",
    },
    loaderOverlay: {
      position: "absolute",
      inset: 0,
      zIndex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    loadingContent: {
      opacity: 0.55,
      filter: "grayscale(1)",
      pointerEvents: "none",
      transition: "opacity 180ms ease, filter 180ms ease",
    },
  })
);

/**
 * Component for adding bookings for organization members.
 * @param {AddMembershipProps} organizationId, onClose - The component props.
 */
const AddOrganizationBooking = ({ organizationId, onClose }: AddMembershipProps) => {
  const { t } = useTranslation(["common", "organization", "snackbars"]);
  const dispatch = useDispatch();
  const classes = useStyles();
  const [selectedMembers, setSelectedMembers] = useState<OrganizationBookingComponent[]>(
    []
  );
  const [selectedEvents, setSelectedEvents] = useState<OrganizationBookingComponent[]>(
    []
  );
  const [failureDialogueOpen, setFailureDialogueOpen] = useState<boolean>(false);
  const [existingBookings, setExistingBookings] = useState<
    OrganizationBookingFailData[]
  >([]);
  const [mismatchingBookings, setMismatchingBookings] = useState<
    OrganizationBookingFailData[]
  >([]);
  const [miscErrorBookings, setMiscErrorBookings] = useState<
    OrganizationBookingFailData[]
  >([]);
  const [anyBookingsSuccessful, setAnyBookingsSuccessful] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const memberships = useSelector((state: AppState) => state.memberships.memberships);

  const events = useSelector(
    (state: AppState) => state.organizationEventsForBooking.events
  );

  /**
   * Fetches the latest membership for the given organization.
   */
  const fetchMembershipsData = () => {
    dispatch(fetchMembershipsByOrganizationId(organizationId));
  };

  /**
   * Fetches the events available for organization booking.
   */
  const fetchEventsForOrganizationBookingData = () => {
    dispatch(fetchOrganizationEventsForBooking());
  };

  /**
   * useEffect hook to fetch membership and events data
   * when the component mounts or when the `organizationId` changes.
   */
  useEffect(() => {
    fetchMembershipsData();
    fetchEventsForOrganizationBookingData();
  }, [organizationId, dispatch]);

  const membershipOptions = memberships.map((member) => ({
    id: member.user,
    name: member.display_name,
  }));

  /**
   * Updates the state with the latest selected organization members for booking.
   *
   * @param newSelectedMembers - An array of selected organization members to be booked for.
   */
  const handleSelectedMembersChange = (
    newSelectedMembers: OrganizationBookingComponent[]
  ) => {
    setSelectedMembers(newSelectedMembers);
  };

  /**
   * Updates the state with the latest selected events for booking.
   *
   * @param newSelectedEvents - An array of selected events to be booked.
   */
  const handleSelectedEventsChange = (
    newSelectedEvents: OrganizationBookingComponent[]
  ) => {
    setSelectedEvents(newSelectedEvents);
  };

  /**
   * Handles the submission of bulk event bookings for selected members and events.
   *
   * Sends a booking request to the backend for selected member and event IDs
   * and handles the response. If there are existing bookings or mismatched target groups,
   * a failure dialog is shown.
   */
  const handleSubmit = async () => {
    if (isSubmitting) return;

    const userIds = selectedMembers.map((member) => member.id);
    const eventIds = selectedEvents.map((event) => event.id);

    setIsSubmitting(true);

    const responseData = await organizationBulkBookingService.bulkBookEventsForMembers(
      userIds,
      eventIds,
      organizationId
    );

    // Exit early if the booking request failed
    if (responseData === false) {
      setIsSubmitting(false);
      enqueueSnackbar(
        t("backendResponses.error.organizationBulkBookingError", {
          ns: "snackbars",
        }),
        {
          variant: "error",
        }
      );
      return;
    }

    const {
      any_bookings_successful,
      existing_bookings,
      mismatching_target_group_bookings,
      misc_error_bookings,
    } = responseData;

    // Show failure dialog if failed bookings in response
    if (
      existing_bookings.length > 0 ||
      mismatching_target_group_bookings.length > 0 ||
      misc_error_bookings.length > 0
    ) {
      setMiscErrorBookings(misc_error_bookings);
      setExistingBookings(existing_bookings);
      setMismatchingBookings(mismatching_target_group_bookings);
      setAnyBookingsSuccessful(any_bookings_successful);
      setFailureDialogueOpen(true);

      // Close component if booked successfully
    } else {
      setIsSubmitting(false);
      enqueueSnackbar(
        t("backendResponses.success.organizationBulkBookingSuccess", {
          ns: "snackbars",
        }),
        {
          variant: "success",
        }
      );
      onClose();
    }
  };

  /**
   * Handles the confirmation action in the failure dialog.
   *
   * Closes the failure dialog and
   * triggers the onClose callback to close the AddOrganizationBooking component.
   */
  const handleFailureDialogueConfirm = () => {
    setFailureDialogueOpen(false);
    setIsSubmitting(false);
    onClose();
  };

  return (
    <Box className={classes.contentWrapper}>
      {isSubmitting ? (
        <Box className={classes.loaderOverlay}>
          <Loader height={8} />
        </Box>
      ) : null}
      <Box className={isSubmitting ? classes.loadingContent : undefined}>
        <Box
          style={{ paddingTop: "1rem" }}
          aria-label="Mitglieder zur Buchung auswählen"
        >
          <h4>{t("bulkBookings.membersSelectTitle", { ns: "organization" })}</h4>
          <h4>{t("bulkBookings.selectMembers", { ns: "organization" })}</h4>
          <CustomMultiSelectAutocomplete
            options={membershipOptions}
            value={selectedMembers}
            onChange={handleSelectedMembersChange}
          />
        </Box>
        <Box style={{ paddingTop: "1rem" }} aria-label="Kurse zur Buchung auswählen">
          <h4>{t("bulkBookings.selectEvents", { ns: "organization" })}</h4>
          <CustomMultiSelectAutocomplete
            options={events}
            value={selectedEvents}
            onChange={handleSelectedEventsChange}
          />
        </Box>
      </Box>
      <Box
        style={{
          paddingTop: "2rem",
          display: "flex",
          justifyContent: "flex-end",
          gap: "0.5rem",
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          onClick={onClose}
          disabled={isSubmitting}
          aria-label="Cancel booking"
        >
          {t("forms.buttons.cancel", { ns: "common" })}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={
            isSubmitting || selectedMembers.length === 0 || selectedEvents.length === 0
          }
          aria-label="Confirm booking"
        >
          {t("bulkBookings.book", { ns: "organization" })}
        </Button>
      </Box>
      <OrganizationBookingFailureNotedDialog
        open={failureDialogueOpen}
        onClose={handleFailureDialogueConfirm}
        onConfirm={handleFailureDialogueConfirm}
        anyBookingsSuccessful={anyBookingsSuccessful}
        existingBookings={existingBookings}
        mismatchingBookings={mismatchingBookings}
        miscErrorBookings={miscErrorBookings}
      />
    </Box>
  );
};

export default AddOrganizationBooking;
